import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  DEFAULT_PER_PAGE,
  createInitialPagination,
} from "../../utils/pagination";

const buildFetchOptions = ({ signal } = {}) => ({
  method: "GET",
  headers: { Accept: "application/json" },
  credentials: "same-origin",
  ...(signal ? { signal } : {}),
});

export default function useMailMagazines() {
  const [mailMagazines, setMailMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(
    createInitialPagination(DEFAULT_PER_PAGE),
  );

  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchMailMagazines = useCallback(async (page = 1) => {
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);

    try {
      const res = await fetch(
        `/admin/mail_magazines.json?page=${page}`,
        buildFetchOptions({ signal: controller.signal }),
      );

      if (!res.ok) throw new Error();

      const data = await res.json();

      if (requestId !== requestIdRef.current) return;

      setMailMagazines(data.mail_magazines || []);
      setPagination(
        data.pagination || createInitialPagination(DEFAULT_PER_PAGE),
      );
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("メルマガ一覧の取得に失敗しました。");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchMailMagazines(1);
    return () => abortRef.current?.abort();
  }, [fetchMailMagazines]);

  return { mailMagazines, loading, pagination, fetchMailMagazines };
}
